// src/components/Common/StatusPill/StatusPill.jsx
import React from 'react';
import './StatusPill.less';

/**
 * StatusPill Component
 * Displays the current charger status with consistent mapping and styles
 *
 * Status Mapping:
 * - Available → "Ready to Charge" (green)
 * - Preparing/Charging/SuspendedEVSE/SuspendedEV/Finishing → "In Use" (blue)
 * - Unavailable/Occupied/Busy/Disabled/Faulted/Reserved → "Unavailable" (orange/red)
 * - Unverified → "Availability unverified" (grey) — surfaced when
 *   the detailed availability endpoint failed. Deliberately NOT
 *   "Ready to Charge" because we cannot confirm chargeability.
 *   The CTA remains enabled and re-verifies on tap; see
 *   deriveChargerDecision.js for the canonical rules.
 * - Unknown/null → "Checking status..." (grey)
 */
/**
 * Resolve a raw operational status to { displayText, statusClass }.
 *
 * Exported as a named function so `deriveChargerDecision.js` (and
 * anything else that wants to report the badge label out-of-band —
 * telemetry, debug logs, tests) can derive the SAME string StatusPill
 * would render, without a React render step. Single source of truth
 * for the badge mapping.
 */
export function resolveBadgeInfo(status) {
  const normalized = status?.trim() || null;
  switch (normalized) {
    case 'Available':
      return { displayText: 'Ready to Charge', statusClass: 'status-available' };
    case 'Preparing':
    case 'Charging':
    case 'SuspendedEVSE':
    case 'SuspendedEV':
    case 'Finishing':
      return { displayText: 'In Use', statusClass: 'status-in-use' };
    case 'Unavailable':
    case 'Occupied':
    case 'Busy':
    case 'Disabled':
    case 'Faulted':
    case 'Reserved':
      return { displayText: 'Unavailable', statusClass: 'status-unavailable' };
    case 'Unverified':
      // Detailed availability check hasn't confirmed the charger is
      // usable. Deliberately explicit — never falsely-green — so the
      // badge (unverified) and the CTA (invariant "Get Started") never
      // contradict each other on the profile screen.
      return { displayText: 'Availability unverified', statusClass: 'status-unknown' };
    case null:
    case undefined:
    case '':
    default:
      return { displayText: 'Checking status...', statusClass: 'status-unknown' };
  }
}

/** Convenience: resolve just the display text. */
export function resolveBadgeLabel(status) {
  return resolveBadgeInfo(status).displayText;
}

const StatusPill = ({ status, className = '' }) => {
  const { displayText, statusClass } = resolveBadgeInfo(status);
  return (
    <span className={`status-pill ${statusClass} ${className}`}>
      {displayText}
    </span>
  );
};

export default StatusPill;
